import { DrTimeAssets, DrTimeImage, DrTimeTests, ExamStep } from "./DrTimeAssets"
import { PatientType } from "./DrTimeTypes"


export interface CheckboxChoice {
    text: string,
    isCorrect: boolean
}

export interface TobaccoUseQuestionChoices {
    text: string,
    choices: Array<CheckboxChoice>
}

export interface TobaccoUseChoices {
    useTobaccoChoices: TobaccoUseQuestionChoices
    yearsUsedTobaccoChoices: TobaccoUseQuestionChoices
    timesUsedTobaccoPerDayChoices: TobaccoUseQuestionChoices
}

export interface Visit {
    visitTitle: string
    dateText: string
    image: DrTimeImage
    audio: string
    notes: Array<Array<string>>
    noteStepTimes: Array<number>
}

export interface Diagnosis {
    symptoms: Array<string>
    symptomsStepTimes: Array<number>
    diagnosisText: string
    diagnosisNotes: Array<string>
    diagnosisNotesStepTimes: Array<number>
}

export interface PatientInfo {
    type: PatientType
    image: DrTimeImage.PATIENT_VAPER | DrTimeImage.PATIENT_SMOKER
    name: string
    symptoms: string
    tobaccoUse: string
    tobaccoUseRecord: string
    symptomChoices: Array<CheckboxChoice>
    tobaccoUseChoices: TobaccoUseChoices
    chartData: Array<Visit>
    correctTest: DrTimeTests
    diagnosis: Diagnosis
}

export const DrTimePatientInfo: Array<PatientInfo> = [
    {
        type: PatientType.VAPER,
        image: DrTimeImage.PATIENT_VAPER,
        name: "Alexis Palmer",
        symptoms: "I’ve been feeling really tired lately. I can’t catch my breath, even just walking. And I have this cough that won’t go away.",
        tobaccoUse: "I’ve been vaping every day for about 5 years. I vape less than 10 times a day.",
        tobaccoUseRecord: "Click on the boxes to record the patient’s vaping history.",
        symptomChoices: [
            { text: "Cough", isCorrect: true },
            { text: "Tiredness", isCorrect: true },
            { text: "Shortness of breath", isCorrect: true },
            { text: "Sore throat", isCorrect: false },
            { text: "Mouth sores", isCorrect: false },
            { text: "Stomach ache", isCorrect: false },
            { text: "Weakness", isCorrect: false },
            { text: "Chest pain", isCorrect: false }
        ],
        tobaccoUseChoices: {
            useTobaccoChoices: {
                text: "Use e-cigarettes:",
                choices: [
                    { text: "Yes", isCorrect: true },
                    { text: "No", isCorrect: false }
                ],
            },
            yearsUsedTobaccoChoices: {
                text: "Years vaped:",
                choices: [
                    { text: "1", isCorrect: false },
                    { text: "5", isCorrect: true },
                    { text: "10", isCorrect: false }
                ]
            },
            timesUsedTobaccoPerDayChoices: {
                text: "Times vaped per day:",
                choices: [
                    { text: "10 or fewer", isCorrect: true },
                    { text: "More than 10", isCorrect: false }
                ]
            }
        },
        chartData: [
            {
                visitTitle: "Visit 1",
                dateText: "5 years ago",
                image: DrTimeImage.LUNGS_VAPING_1,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.FIVE_YEARS, PatientType.VAPER),
                notes: [
                    ["· Airways very", "  clear"],
                    ["· Tissue clean and", "  healthy"],
                    ["· Easy for patient", "  to breathe"],
                    ["· Patient gets better", "  from a cold right", "  away"]
                ],
                noteStepTimes: [2, 4.5, 6.5, 8.5]
            },
            {
                visitTitle: "Visit 2",
                dateText: "3 years ago",
                image: DrTimeImage.LUNGS_VAPING_2,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.THREE_YEARS, PatientType.VAPER),
                notes: [
                    ["· Airways somewhat", "  clear"],
                    ["· Some lung tissue", "  damaged by vaping"],
                    ["· Patient takes week", "  or two to get better", "  from a cold"]
                ],
                noteStepTimes: [2, 4.5, 10]
            },
            {
                visitTitle: "Visit 3",
                dateText: "1 year ago",
                image: DrTimeImage.LUNGS_VAPING_3,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.ONE_YEAR, PatientType.VAPER),
                notes: [
                    ["· Airways starting to", "  shrink"],
                    ["· Airways getting red", "  and swollen"],
                    ["· Persistent cough"]
                ],
                noteStepTimes: [1.7, 4, 6.5]
            },
            {
                visitTitle: "X-Ray",
                dateText: "Today",
                image: DrTimeImage.LUNGS_VAPING_4,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.DIAGNOSIS_1, PatientType.VAPER),
                notes: [
                    ["· Airways very small"],
                    ["· Airways very red", "  and swollen"],
                    ["· Patient coughs all", "  the time"]
                ],
                noteStepTimes: [1.1, 2.5, 10]
            }
        ],
        correctTest: DrTimeTests.TEST_XRAY,
        diagnosis: {
            symptoms: [
                "· Cough",
                "· Tiredness",
                "· Shortness of breath",
                "· Vaper",
                "· Increasing airway damage"
            ],
            symptomsStepTimes: [7.2, 8, 9.5, 11.5, 15],
            diagnosisText: "Possible chronic bronchitis",
            diagnosisNotes: [
                "· Needs treatment",
                "· Quit vaping",
                "· Tell doctor"
            ],
            diagnosisNotesStepTimes: [6, 8.5, 11],
        }
    },
    {
        type: PatientType.SMOKER,
        image: DrTimeImage.PATIENT_SMOKER,
        name: "Shawn Johnson",
        symptoms: "Lately I’ve been feeling this pain in my chest. It’s not really bad, but it feels kind of tight. I’m also really tired. Plus, I feel weak. I can barely help my wife carry the groceries in from the car.",
        tobaccoUse: "I’ve been smoking for about 10 years. But I smoke only a few cigarettes a day; no more than 10.",
        tobaccoUseRecord: "Click on the boxes to record the patient’s tobacco use.",
        symptomChoices: [
            {text: "Cough", isCorrect: false},
            {text: "Tiredness", isCorrect: true},
            {text: "Shortness of breath", isCorrect: false},
            {text: "Sore throat", isCorrect: false},
            {text: "Mouth sores", isCorrect: false},
            {text: "Stomach ache", isCorrect: false},
            {text: "Weakness", isCorrect: true},
            {text: "Chest pain", isCorrect: true}
        ],
        tobaccoUseChoices: {
            useTobaccoChoices: {
                text: "Smoke cigarettes:",
                choices: [
                    { text: "Yes", isCorrect: true },
                    { text: "No", isCorrect: false }
                ],
            },
            yearsUsedTobaccoChoices: {
                text: "Years smoked:",
                choices: [
                    { text: "1", isCorrect: false },
                    { text: "5", isCorrect: false },
                    { text: "10", isCorrect: true }
                ]
            },
            timesUsedTobaccoPerDayChoices: {
                text: "Cigarettes per day:",
                choices: [
                    { text: "10 or fewer", isCorrect: true },
                    { text: "More than 10", isCorrect: false }
                ]
            }
        },
        chartData: [
            {
                visitTitle: "Visit 1",
                dateText: "10 years ago",
                image: DrTimeImage.HEART_SMOKING_1,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.TEN_YEARS, PatientType.SMOKER),
                notes: [
                    ["· Heart normal"],
                    ["· Arteries clear"],
                    ["· Blood flowing well"]
                ],
                noteStepTimes: [2, 7, 8]
            },
            {
                visitTitle: "Visit 2",
                dateText: "5 years ago",
                image: DrTimeImage.HEART_SMOKING_2,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.FIVE_YEARS, PatientType.SMOKER),
                notes: [
                    ["· Fatty deposits in", "  arteries"],
                    ["· Heart working", "  harder"],
                    ["· Blood flow", "  decreased"]
                ],
                noteStepTimes: [1.8, 5, 7]
            },
            {
                visitTitle: "Visit 3",
                dateText: "1 year ago",
                image: DrTimeImage.HEART_SMOKING_3,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.ONE_YEAR, PatientType.SMOKER),
                notes: [
                    ["· Arteries clogged"],
                    ["· Feeling tired"],
                    ["· Feeling weak"]
                ],
                noteStepTimes: [4, 6, 6.8]
            },
            {
                visitTitle: "Scan",
                dateText: "Today",
                image: DrTimeImage.HEART_SMOKING_4,
                audio: DrTimeAssets.findExamStepAudio(ExamStep.DIAGNOSIS_1, PatientType.SMOKER),
                notes: [
                    ["· Arteries very", "  clogged"],
                    ["· Feeling very tired"],
                    ["· Feeling very weak"],
                    ["· Chest pains"]
                ],
                noteStepTimes: [1.3, 4, 4.5, 8.8]
            }
        ],
        correctTest: DrTimeTests.TEST_SCANNER,
        diagnosis: {
            symptoms: [
                "· Chest pain",
                "· Tiredness",
                "· Weakness",
                "· Smoker",
                "· Arteries more clogged"
            ],
            symptomsStepTimes: [8, 9, 9.7, 11, 15.5],
            diagnosisText: "At risk for heart attack",
            diagnosisNotes: [
                "· Needs surgery",
                "· Quit smoking",
                "· Share information with doctor"
            ],
            diagnosisNotesStepTimes: [10.8, 13.8, 16],
        }
    }
]